import React, { FC, ReactElement, useEffect } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  ReferAFriend as UIFoundationsAskAFriend,
  Container as UIFoundationsContainer,
  TextHorizontalAlignments,
  InputTextField as UIFoundationsInputTextField,
  InputTextFieldStates,
  ContainerWidths,
  ReferItemProps,
  useThemeUtils
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { REPLACE_MESSAGE_TO_SHARE } from '../../../../utils/constants'
import AskAFriendEditConfig, { AskAFriendProps } from './AskAFriendEditConfig'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import {
  getChannelEntries,
  handleCopyOption,
  SuccessCopyMessageComponent
} from '../common/utils'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'

const AdditionalContentForCopyInputField = ({
  defaultValue,
  'aria-label': ariaLabel
}: {
  defaultValue?: string
  'aria-label'?: string
}): ReactElement => {
  const themeUtils = useThemeUtils()

  const configInputField = {
    defaultPadding: false,
    defaultValue,
    multiline: true,
    fullWidth: true,
    textAlign: TextHorizontalAlignments.Center,
    state: InputTextFieldStates.ReadOnly,
    inputProps: {
      'aria-label': ariaLabel
    }
  }
  return (
    <UIFoundationsContainer
      width={themeUtils.getResponsiveProp([
        ContainerWidths.Full,
        ContainerWidths.Full,
        '600px'
      ])}
    >
      <UIFoundationsInputTextField {...configInputField} />
    </UIFoundationsContainer>
  )
}

const AskAFriend: FC<EditableComponentPropsType<AskAFriendProps>> =
  withComponentRefresh((props) => {
    const {
      id,
      apiUrl,
      messageToShare,
      nativeShareDescriptiveText,
      nativeShareCtaLabel,
      communicationChannels,
      copy,
      background,
      ...rest
    } = props.model
    const editableProps = useExtractEditableProps(props, rest)
    const [navigatorCanShareMessage, setNavigatorCanShareMessage] =
      React.useState(false)
    const themeUtils = useThemeUtils()

    const layoutWidths = themeUtils.getBreakpointsValues(
      themeUtils.getResponsiveProp([null, null, null, 0, null])
    )

    const lgBreakpoint = Object.keys(layoutWidths)?.find(
      (key) => layoutWidths[key as keyof typeof layoutWidths] === 0
    )

    const shareData = {
      text: messageToShare
    }

    const onClickCopy = () => {
      navigator.clipboard.writeText(messageToShare || '')

      return <SuccessCopyMessageComponent content={copy?.successMessage} />
    }

    useEffect(() => {
      const handleResize = () => {
        if ('share' in navigator) {
          const canNavigatorShare = navigator.canShare(shareData)
          if (window.innerWidth < Number(lgBreakpoint) && canNavigatorShare) {
            setNavigatorCanShareMessage(true)
          } else {
            setNavigatorCanShareMessage(false)
          }
        }
      }

      window.addEventListener('resize', handleResize)
      handleResize()
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    const encodeMessageToShare =
      messageToShare && encodeURIComponent(messageToShare)

    const channelEntries: Array<ReferItemProps> = getChannelEntries(
      communicationChannels || [],
      REPLACE_MESSAGE_TO_SHARE,
      encodeMessageToShare
    )

    if (copy && Object.keys(copy).length > 0) {
      let copyIndex: number = channelEntries?.length

      if (copy.position && copy.position - 1 <= channelEntries?.length) {
        copyIndex = copy.position - 1
      }

      channelEntries?.splice(
        copyIndex,
        0,
        handleCopyOption(
          copy,
          <AdditionalContentForCopyInputField
            defaultValue={messageToShare}
            aria-label={nativeShareCtaLabel}
          />,
          onClickCopy
        )
      )
    }
    const handleNavigatorShare = async () => {
      if (navigator?.share) {
        try {
          await navigator.share(shareData)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    }

    const configAskAFriend = {
      entries: channelEntries,
      nativeShareOnClick: handleNavigatorShare,
      nativeShare: navigatorCanShareMessage,
      shareWithFriendLabel: nativeShareCtaLabel,
      descriptiveTextNativeShare: nativeShareDescriptiveText
    }

    return (
      <EditableComponent {...editableProps}>
        {AskAFriendEditConfig.isEmpty(props?.model) ? (
          AskAFriendEditConfig.emptyLabel
        ) : (
          <UIFoundationsContainer background={background}>
            <UIFoundationsAskAFriend {...configAskAFriend} />
          </UIFoundationsContainer>
        )}
      </EditableComponent>
    )
  })

const AskAFriendWithErrorBoundary = withErrorBoundary(AskAFriend, {
  componentName: 'AskAFriend',
  fallback: <ErrorFallback componentName="AskAFriend" />
})

export default AskAFriendWithErrorBoundary
